import React, { useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Google as GoogleIcon,
  Apple as AppleIcon,
  Key as KeyIcon,
  Phone as PhoneIcon,
  Lock as LockIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  QrCode2 as QrCodeIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { PasskeyManager } from './PasskeyManager';
import { QRCodeSVG } from 'qrcode.react';
import { totpService } from '../../services/totp';

const AUTH_METHOD_ICONS = {
  google: <GoogleIcon />,
  apple: <AppleIcon />,
  passkey: <KeyIcon />,
  phone: <PhoneIcon />,
  totp: <QrCodeIcon />,
};

const AUTH_METHOD_NAMES = {
  google: 'Google',
  apple: 'Apple',
  passkey: 'Passkey',
  phone: 'Phone',
  totp: 'Authenticator App'
} as const;

const neonGreen = '#34C759';

interface AuthMethods {
  passkey?: {
    credentials: {
      id: string;
      userId: string;
      credentialId: string;
      publicKey: string;
      counter: number;
      createdAt: string;
      lastUsedAt: string;
    }[];
    createdAt: string;
    lastVerifiedAt: string;
  };
  totp?: {
    secret: string;
    createdAt: string;
    lastVerifiedAt: string;
  };
  phone?: {
    number: string;
    verified: boolean;
    createdAt: string;
    lastVerifiedAt: string;
  };
  google?: boolean;
  apple?: boolean;
}

const getMethodIcon = (method: keyof typeof AUTH_METHOD_NAMES) => {
  switch (method) {
    case 'google':
      return <GoogleIcon />;
    case 'apple':
      return <AppleIcon />;
    case 'passkey':
      return <KeyIcon />;
    case 'phone':
      return <PhoneIcon />;
    case 'totp':
      return <QrCodeIcon />;
    default:
      return <></>;
  }
};

const generateTOTPSecret = async () => {
  // Implement your own logic to generate a TOTP secret
  // For demonstration purposes, a random string is generated
  return Math.random().toString(36).substr(2, 16);
};

const generateQRCode = (secret: string) => {
  // Implement your own logic to generate a QR code
  // For demonstration purposes, a dummy QR code is generated
  return `https://example.com/qr-code?secret=${secret}`;
};

export const AuthMethodsManager = () => {
  const { state, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openTOTPDialog, setOpenTOTPDialog] = useState(false);
  const [totpSecret, setTotpSecret] = useState<string | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState('');

  const getEnabledMethods = () => {
    if (!state.user?.authMethods) return [];

    const methods: (keyof typeof AUTH_METHOD_NAMES)[] = [];
    const authMethods = state.user.authMethods as AuthMethods;

    if (authMethods.passkey?.credentials?.length ?? 0 > 0) methods.push('passkey');
    if (authMethods.totp?.secret) methods.push('totp');
    if (authMethods.phone?.verified) methods.push('phone');
    if (authMethods.google) methods.push('google');
    if (authMethods.apple) methods.push('apple');

    return methods;
  };

  const getMethodStatus = (method: keyof typeof AUTH_METHOD_NAMES) => {
    if (!state.user?.authMethods) return null;

    const authMethods = state.user.authMethods as AuthMethods;
    switch (method) {
      case 'passkey':
        const credentialsLength = authMethods.passkey?.credentials?.length ?? 0;
        return credentialsLength > 0 
          ? `${credentialsLength} device(s)`
          : null;
      case 'totp':
        return authMethods.totp?.createdAt ? 'Enabled' : null;
      case 'phone':
        return authMethods.phone?.verified ? authMethods.phone.number : null;
      case 'google':
        return authMethods.google ? 'Connected' : null;
      case 'apple':
        return authMethods.apple ? 'Connected' : null;
      default:
        return null;
    }
  };

  const handleMethodAction = async (method: keyof typeof AUTH_METHOD_NAMES) => {
    switch (method) {
      case 'totp':
        handleSetupTOTP();
        break;
      default:
        handleRemoveMethod(method);
        break;
    }
  };

  const handleSetupTOTP = async () => {
    try {
      setLoading(true);
      const secret = await generateTOTPSecret();
      
      // Update user's authMethods with new TOTP
      if (state.user) {
        await updateUser(state.user.id, {
          authMethods: {
            ...state.user.authMethods,
            totp: {
              secret,
              createdAt: new Date().toISOString(),
              lastVerifiedAt: new Date().toISOString()
            }
          }
        });
      }

      setTotpSecret(secret);
      setQrCode(generateQRCode(secret));
      setOpenTOTPDialog(true);
    } catch (error) {
      console.error('Error setting up TOTP:', error);
      setError('Failed to set up authenticator app');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyTOTP = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!state.user?.email) {
        throw new Error('User email not found');
      }

      // Verify TOTP code
      const isValid = await totpService.verifyTOTP(state.user.email, totpCode);
      if (isValid && state.user) {
        const now = new Date().toISOString();
        await updateUser(state.user.id, {
          authMethods: {
            ...state.user.authMethods,
            totp: {
              secret: state.user.authMethods.totp?.secret || '',
              createdAt: state.user.authMethods.totp?.createdAt || now,
              lastVerifiedAt: now
            }
          }
        });
        setOpenTOTPDialog(false);
        setSuccess('TOTP verification successful');
      } else {
        setError('Invalid TOTP code');
      }
    } catch (error) {
      setError('Failed to verify TOTP code');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMethod = async (method: string) => {
    try {
      setLoading(true);
      if (!state.user?.authMethods) return;

      // Don't allow removing the last authentication method
      const enabledCount = Object.keys(state.user.authMethods).length;
      if (enabledCount <= 1) {
        setError('Cannot remove the last authentication method');
        return;
      }

      const updatedAuthMethods = { ...state.user.authMethods };
      delete updatedAuthMethods[method as keyof typeof updatedAuthMethods];

      await updateUser(state.user.id, {
        authMethods: updatedAuthMethods
      });

      setSuccess(`${AUTH_METHOD_NAMES[method as keyof typeof AUTH_METHOD_NAMES]} removed successfully`);
    } catch (error) {
      console.error('Error removing auth method:', error);
      setError('Failed to remove authentication method');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ color: neonGreen }}>
        Authentication Methods
      </Typography>
      
      <Alert severity="info" sx={{ mb: 2 }}>
        💡 Each device needs its own authentication method. Set up a passkey for this device and alternative methods for other devices.
      </Alert>

      <List>
        {Object.entries(AUTH_METHOD_NAMES).map(([method, name]) => {
          const methodKey = method as keyof typeof AUTH_METHOD_NAMES;
          const status = getMethodStatus(methodKey);
          const isEnabled = status !== null;

          return (
            <ListItem key={method}>
              <ListItemIcon sx={{ color: neonGreen }}>
                {getMethodIcon(methodKey)}
              </ListItemIcon>
              <ListItemText
                primary={name}
                secondary={
                  methodKey === 'passkey' 
                    ? (status ? 'Configured for this device' : 'Not configured for this device')
                    : (status || 'Not configured')
                }
                sx={{ color: 'white' }}
              />
              <ListItemSecondaryAction>
                {methodKey === 'passkey' ? (
                  <PasskeyManager />
                ) : (
                  <Button
                    variant="outlined"
                    color={isEnabled ? 'error' : 'primary'}
                    onClick={() => handleMethodAction(methodKey)}
                    startIcon={isEnabled ? <DeleteIcon /> : <AddIcon />}
                  >
                    {isEnabled ? 'Remove' : 'Add'}
                  </Button>
                )}
              </ListItemSecondaryAction>
            </ListItem>
          );
        })}
      </List>

      {!getEnabledMethods().includes('passkey') && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          ⚠️ Setting up a passkey for this device is recommended for the highest level of security.
        </Alert>
      )}

      {/* TOTP Dialog */}
      <Dialog open={openTOTPDialog} onClose={() => setOpenTOTPDialog(false)}>
        <DialogTitle>Set Up Authenticator App</DialogTitle>
        <DialogContent>
          {qrCode && (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <QRCodeSVG value={qrCode} size={200} />
            </Box>
          )}
          <Typography variant="body2" gutterBottom>
            Scan this QR code with your authenticator app (e.g., Google Authenticator, Authy).
          </Typography>
          <TextField
            margin="dense"
            label="Verification Code"
            fullWidth
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTOTPDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleVerifyTOTP}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            Verify
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
